import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  CircularProgress,

  Badge,
  Chip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  TrendingDown as TrendingDownIcon,
  ShoppingCart as ShoppingCartIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  NotificationImportant as AlertIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { InventoryMetrics, InventoryAlert, ReorderSuggestion, InventoryItem } from '../types';
import inventoryService from '../services/inventoryService';
import { InventoryTable } from './InventoryTable';
import { InventoryAlerts } from './InventoryAlerts';
import { ReorderSuggestions } from './ReorderSuggestions';
import { InventoryAnalytics, LowStockItems, ExpiringProducts } from './PlaceholderComponents';
import { InventoryDetailModal } from './InventoryDetailModal';
import { CreatePurchaseOrder } from './CreatePurchaseOrder';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-tabpanel-${index}`}
      aria-labelledby={`inventory-tab-${index}`}
      {...other}
    >
      {value !== index ? null : <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const InventoryDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<InventoryMetrics | null>(null);
  const [alerts, setAlerts] = useState<InventoryAlert[]>([]);
  const [reorderSuggestions, setReorderSuggestions] = useState<ReorderSuggestion[]>([]);
  const [lowStockItems, setLowStockItems] = useState<InventoryItem[]>([]);
  const [expiringItems, setExpiringItems] = useState<InventoryItem[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [createPOOpen, setCreatePOOpen] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [
        metricsData,
        alertsData,
        reorderData,
        lowStockData,
        expiringData
      ] = await Promise.all([
        inventoryService.getInventoryMetrics(),
        inventoryService.getInventoryAlerts(true),
        inventoryService.getReorderSuggestions(),
        inventoryService.getLowStockItems(),
        inventoryService.getExpiringItems(90)
      ]);

      setMetrics(metricsData);
      setAlerts(alertsData);
      setReorderSuggestions(reorderData);
      setLowStockItems(lowStockData);
      setExpiringItems(expiringData);
    } catch (error) {
      console.error('Error loading inventory dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      const blob = await inventoryService.exportInventory(format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting inventory:', error);
    }
  };

  const handleViewDetails = (sku: string) => {
    setSelectedSku(sku);
    setDetailModalOpen(true);
  };

  const handleCreatePurchaseOrder = () => {
    if (reorderSuggestions.length === 0) {
      return; // No suggestions available
    }
    setCreatePOOpen(true);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Inventory Management
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          {reorderSuggestions.length > 0 && (
            <Button
              startIcon={<ShoppingCartIcon />}
              onClick={handleCreatePurchaseOrder}
              variant="contained"
              color="primary"
              sx={{ mr: 1 }}
            >
              Create Purchase Orders ({reorderSuggestions.length})
            </Button>
          )}
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('excel')}
            variant="outlined"
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Critical Alerts */}
      {alerts.filter(a => a.Severity === 'HIGH').length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Critical Inventory Alerts</AlertTitle>
          {alerts.filter(a => a.Severity === 'HIGH').slice(0, 3).map(alert => (
            <Box key={alert.AlertID}>• {alert.Message}</Box>
          ))}
        </Alert>
      )}

      {/* Metrics Cards */}
      {metrics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <InventoryIcon color="primary" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    Total SKUs
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {formatNumber(metrics.totalSKUs)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Value: {formatCurrency(metrics.totalValue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <WarningIcon color="error" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    Stock Alerts
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`${metrics.outOfStockCount} Out`} 
                    color="error" 
                    size="small" 
                  />
                  <Chip 
                    label={`${metrics.lowStockCount} Low`} 
                    color="warning" 
                    size="small" 
                  />
                  <Chip 
                    label={`${metrics.expiringCount} Expiring`} 
                    color="info" 
                    size="small" 
                  />
                </Box>
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {metrics.outOfStockCount + metrics.lowStockCount} items need attention
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingDownIcon color="warning" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    Dead Inventory
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {formatCurrency(metrics.deadInventoryValue)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {metrics.slowMovingCount} slow-moving items
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TimelineIcon color="success" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    Inventory Health
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {(metrics.turnoverRate * 100).toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Avg {metrics.averageDaysOfSupply} days of supply
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab 
              label="All Inventory" 
              icon={<Badge badgeContent={metrics?.totalSKUs} max={9999} color="primary">
                <InventoryIcon />
              </Badge>}
            />
            <Tab 
              label="Alerts" 
              icon={<Badge badgeContent={alerts.length} color="error">
                <AlertIcon />
              </Badge>}
            />
            <Tab 
              label="Reorder Suggestions" 
              icon={<Badge badgeContent={reorderSuggestions.length} color="warning">
                <ShoppingCartIcon />
              </Badge>}
            />
            <Tab 
              label="Low Stock" 
              icon={<Badge badgeContent={lowStockItems.length} color="warning">
                <WarningIcon />
              </Badge>}
            />
            <Tab 
              label="Expiring Products" 
              icon={<Badge badgeContent={expiringItems.length} color="info">
                <WarningIcon />
              </Badge>}
            />
            <Tab 
              label="Analytics" 
              icon={<AssessmentIcon />}
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by SKU, product name, brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <InventoryTable 
            searchTerm={searchTerm} 
            onViewDetails={handleViewDetails}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <InventoryAlerts alerts={alerts} onResolve={loadDashboardData} />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <ReorderSuggestions 
            suggestions={reorderSuggestions} 
            onCreatePurchaseOrder={handleCreatePurchaseOrder}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <LowStockItems items={lowStockItems} />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <ExpiringProducts items={expiringItems} />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <InventoryAnalytics />
        </TabPanel>
      </Card>

      {/* Modals */}
      <InventoryDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedSku(null);
        }}
        sku={selectedSku}
      />

      <CreatePurchaseOrder
        open={createPOOpen}
        onClose={() => setCreatePOOpen(false)}
        suggestions={reorderSuggestions}
      />
    </Box>
  );
};
